import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useAuth } from "../providers/AuthContext";
import HeaderBar from "../components/HeaderBar";
import Sidebar from "../components/Sidebar";
import Breadcrumbs from "../components/Breadcrumbs";
import DatePicker from "../components/DatePicker";
import MultipleSelectDropdown from "../components/MultipleSelectDropdown";
import ReactApexChart from "react-apexcharts";
import { getOverviewData, getOfficeBranches, getAgreementNames, getBillingGroupNames } from "../api/Overview";
import toast from "react-hot-toast";

const BillingOverviewPage = () => {
  const { getAccessToken } = useAuth();

  // Filter states
  const [selectedOfficeBranches, setSelectedOfficeBranches] = useState([]);
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setFullYear(date.getFullYear() - 2);
    return date;
  });
  const [endDate, setEndDate] = useState(() => {
    const date = new Date();
    date.setMonth(date.getMonth() + 1);
    return date;
  });
  const [selectedAgreements, setSelectedAgreements] = useState([]);
  const [selectedBillingGroups, setSelectedBillingGroups] = useState([]);

  // Data states
  const [overviewData, setOverviewData] = useState(null);
  const [officeBranches, setOfficeBranches] = useState([]);
  const [agreements, setAgreements] = useState([]);
  const [billingGroups, setBillingGroups] = useState([]);
  const [loading, setLoading] = useState(true);

  // Chart states
  const [chartSeries, setChartSeries] = useState([]);
  const [chartCategories, setChartCategories] = useState([]);

  const fetchInitialData = useCallback(async () => {
    try {
      const accessToken = await getAccessToken();

      const [branchesData, agreementsData, billingGroupsData] = await Promise.all([
        getOfficeBranches(accessToken),
        getAgreementNames(accessToken),
        getBillingGroupNames(accessToken),
      ]);

      setOfficeBranches(branchesData.map(b => ({ label: b, value: b })));
      setAgreements(agreementsData.map(a => ({
        label: a.name,
        value: a.name,
        officeBranch: a.officeBranch,
      })));
      setBillingGroups(billingGroupsData.map(bg => ({
        label: bg.name,
        value: bg.name,
        officeBranch: bg.officeBranch,
        agreementName: bg.agreementName,
      })));

      // Select all options by default
      setSelectedOfficeBranches(branchesData);
      setSelectedAgreements(agreementsData.map(a => a.name));
      setSelectedBillingGroups(billingGroupsData.map(bg => bg.name));

      setLoading(false);
    } catch (error) {
      console.error("Failed to fetch initial data:", error);
      toast.error("Failed to load filter options");
      setLoading(false);
    }
  }, [getAccessToken]);

  const fetchOverviewData = useCallback(async () => {
    try {
      const accessToken = await getAccessToken();

      const filters = {
        officeBranches: selectedOfficeBranches.length > 0 ? selectedOfficeBranches : [],
        dateFrom: startDate ? startDate.toISOString() : null,
        dateTo: endDate ? endDate.toISOString() : null,
        agreementNames: selectedAgreements.length > 0 ? selectedAgreements : [],
        billingGroupNames: selectedBillingGroups.length > 0 ? selectedBillingGroups : [],
      };

      const data = await getOverviewData(filters, accessToken);
      setOverviewData(data);
      prepareChartData(data);
    } catch (error) {
      console.error("Failed to fetch overview data:", error);
      toast.error("Failed to load overview data");
    }
  }, [getAccessToken, selectedOfficeBranches, startDate, endDate, selectedAgreements, selectedBillingGroups]);

  // Fetch dropdown options on mount
  useEffect(() => {
    fetchInitialData();
  }, [fetchInitialData]);

  // Cascade: Agreement options filtered by selected Office Branches
  const availableAgreements = useMemo(() => {
    if (selectedOfficeBranches.length === 0) return agreements;
    return agreements.filter(a => selectedOfficeBranches.includes(a.officeBranch));
  }, [agreements, selectedOfficeBranches]);

  // Cascade: Billing Group options filtered by selected Office Branches + selected Agreements
  const availableBillingGroups = useMemo(() => {
    return billingGroups.filter(bg => {
      const branchMatch = selectedOfficeBranches.length === 0 || selectedOfficeBranches.includes(bg.officeBranch);
      const agreementMatch = selectedAgreements.length === 0 || selectedAgreements.includes(bg.agreementName);
      return branchMatch && agreementMatch;
    });
  }, [billingGroups, selectedOfficeBranches, selectedAgreements]);

  // Clean up selections when parent filters shrink their available options
  useEffect(() => {
    const availableValues = availableAgreements.map(a => a.value);
    setSelectedAgreements(prev => prev.filter(v => availableValues.includes(v)));
  }, [availableAgreements]);

  useEffect(() => {
    const availableValues = availableBillingGroups.map(bg => bg.value);
    setSelectedBillingGroups(prev => prev.filter(v => availableValues.includes(v)));
  }, [availableBillingGroups]);

  // Fetch overview data when filters change
  useEffect(() => {
    if (!loading) {
      fetchOverviewData();
    }
  }, [loading, fetchOverviewData]);

  const prepareChartData = (data) => {
    if (!data || !data.chartData || data.chartData.length === 0) {
      setChartSeries([]);
      setChartCategories([]);
      return;
    }

    // Group by OfficeBranch for series
    const groupedByBranch = data.chartData.reduce((acc, item) => {
      if (!acc[item.officeBranch]) {
        acc[item.officeBranch] = [];
      }
      acc[item.officeBranch].push(item);
      return acc;
    }, {});

    // Get unique dates for categories
    const uniqueDates = [...new Set(data.chartData.map(d => d.date))].sort();

    // Create series for each office branch
    const series = Object.keys(groupedByBranch).map(branch => ({
      name: branch,
      data: uniqueDates.map(date => {
        const item = groupedByBranch[branch].find(d => d.date === date);
        return item ? item.amount : 0;
      })
    }));

    setChartSeries(series);
    setChartCategories(uniqueDates);
  };

  const chartOptions = {
    chart: {
      type: 'bar',
      height: 400,
      stacked: false,
      toolbar: {
        show: true
      }
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: '55%',
        endingShape: 'rounded'
      },
    },
    dataLabels: {
      enabled: false
    },
    stroke: {
      show: true,
      width: 2,
      colors: ['transparent']
    },
    xaxis: {
      categories: chartCategories,
      title: {
        text: 'Date'
      }
    },
    yaxis: {
      title: {
        text: 'Amount (SEK)'
      },
      labels: {
        formatter: (val) => val ? val.toLocaleString('sv-SE') : '0'
      }
    },
    fill: {
      opacity: 1
    },
    tooltip: {
      y: {
        formatter: (val) => val ? val.toLocaleString('sv-SE', { style: 'currency', currency: 'SEK' }) : '0 SEK'
      }
    },
    legend: {
      position: 'top',
      horizontalAlign: 'left'
    }
  };

  return (
    <>
      <HeaderBar />
      <div className="flex flex-row w-full">
        <Sidebar />
        <main className="page">
          <div className={`${loading ? "loading" : ""} page-content`}>
            <Breadcrumbs />

            <div className="flex flex-row items-center pb-4">
              <h2>Billing Overview</h2>
            </div>

            {/* Filters Section */}
            <div className="rounded-md shadow-md border w-full p-4 mb-4 bg-white">
              <h3 className="text-lg font-semibold mb-4">Filters</h3>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <MultipleSelectDropdown
                  label="Office Branch"
                  options={officeBranches}
                  selected={selectedOfficeBranches}
                  onChange={setSelectedOfficeBranches}
                  searchable={true}
                  placeholder="Select office branches..."
                />

                <MultipleSelectDropdown
                  label="Agreement"
                  options={availableAgreements}
                  selected={selectedAgreements}
                  onChange={setSelectedAgreements}
                  searchable={true}
                  placeholder="Select agreements..."
                />

                <MultipleSelectDropdown
                  label="Billing Group"
                  options={availableBillingGroups}
                  selected={selectedBillingGroups}
                  onChange={setSelectedBillingGroups}
                  searchable={true}
                  placeholder="Select billing groups..."
                />

                <div>
                  <label className="block text-sm font-bold text-gray-800 mb-2">
                    Date Range
                  </label>
                  <DatePicker
                    startDate={startDate}
                    endDate={endDate}
                    setStartDate={setStartDate}
                    setEndDate={setEndDate}
                  />
                </div>
              </div>
            </div>

            {/* Summary Cards */}
            {overviewData && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
                <div className="rounded-md shadow-md border p-6 bg-white">
                  <h4 className="text-sm font-semibold text-gray-600 mb-2">
                    Total Billing Amount
                  </h4>
                  <p className="text-3xl font-bold text-primary-700">
                    {overviewData.totalAmount.toLocaleString('sv-SE', {
                      style: 'currency',
                      currency: 'SEK'
                    })}
                  </p>
                </div>

                {overviewData.officeBranchSummaries?.map((branch, index) => (
                  <div key={index} className="rounded-md shadow-md border p-6 bg-white">
                    <h4 className="text-sm font-semibold text-gray-600 mb-2">
                      {branch.officeBranch}
                    </h4>
                    <p className="text-3xl font-bold text-primary-700">
                      {branch.totalAmount.toLocaleString('sv-SE', {
                        style: 'currency',
                        currency: 'SEK'
                      })}
                    </p>
                  </div>
                ))}
              </div>
            )}

            {/* Chart Section */}
            <div className="rounded-md shadow-md border w-full p-6 mb-4 bg-white">
              <h3 className="text-lg font-semibold mb-4">Billing by Office Branch</h3>

              {chartSeries.length > 0 ? (
                <ReactApexChart
                  options={chartOptions}
                  series={chartSeries}
                  type="bar"
                  height={400}
                />
              ) : (
                <div className="flex items-center justify-center h-64 text-gray-500">
                  No data available for the selected filters
                </div>
              )}
            </div>
          </div>
        </main>
      </div>
    </>
  );
};

export default BillingOverviewPage;
